import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import AnimatedSection from 'src/components/AnimatedSection'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'
import { RedirectUrls } from 'src/types/types'

interface IProps {
  pillar: PillarPage
}

interface IRow {
  title: string
  spotMeInsurance: boolean
  otherInsurance: boolean
}

const SpotMeMark = ({ on }: { on: boolean }) =>
  on ? (
    <span className="flex size-6 items-center justify-center rounded-full bg-blue-500 text-white-500">
      <CheckRoundedIcon className="text-[14px]" fontSize="inherit" />
    </span>
  ) : (
    <span className="flex size-6 items-center justify-center rounded-full border border-ink-100/40 text-ink-100">
      <CloseRoundedIcon className="text-[14px]" fontSize="inherit" />
    </span>
  )

const OtherMark = ({ on }: { on: boolean }) =>
  on ? (
    <span className="flex size-6 items-center justify-center rounded-full border border-ink-200 text-ink-200">
      <CheckRoundedIcon className="text-[14px]" fontSize="inherit" />
    </span>
  ) : (
    <span className="flex size-6 items-center justify-center rounded-full border border-ink-100/40 text-ink-100">
      <CloseRoundedIcon className="text-[14px]" fontSize="inherit" />
    </span>
  )

const Comparison = ({ pillar }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const { t: tCommon } = useTranslation('common')
  const rows: IRow[] = t('COMPARISON.ITEMS', {
    returnObjects: true,
    cities: tStats('CITIES')
  })

  return (
    <AnimatedSection
      className="w-full bg-cream-300 py-20 md:py-28"
      variant="slideUp"
    >
      <div className="mx-auto max-w-5xl px-5 md:px-10">
        <section className="md:grid md:grid-cols-12 md:gap-12">
          <div className="md:col-span-4">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {tCommon('PILLAR_SECTIONS.EYEBROW_COMPARISON')}
            </p>
            <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
              {t('COMPARISON.TITLE')}
            </h2>
            <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('COMPARISON.SUBTITLE')}
            </p>
          </div>

          <div className="mt-8 md:col-span-8 md:mt-0">
            <div className="flex items-baseline justify-end gap-4 pb-3 md:gap-10">
              <span className="font-display text-[13px] font-medium text-blue-500 md:text-xs md:tracking-[0.14em] md:uppercase">
                SpotMe
              </span>
              <span className="w-16 text-center text-[12px] font-medium text-ink-200 md:w-24 md:text-xs md:font-normal md:tracking-[0.14em] md:uppercase">
                <span className="md:hidden">
                  {t('COMPARISON.COMPETENCE_SHORT')}
                </span>
                <span className="hidden md:inline">
                  {t('COMPARISON.COMPETENCE')}
                </span>
              </span>
            </div>

            <ul className="border-t border-ink-100/15">
              {rows.map(({ title, spotMeInsurance, otherInsurance }, index) => (
                <li
                  key={`row-${index}`}
                  className="flex items-center justify-between gap-3 border-b border-ink-100/15 py-4 text-[14px] text-ink-700 md:gap-6 md:py-5 md:text-[15px]"
                >
                  <span className="min-w-0 flex-1 pr-1 md:pr-2">{title}</span>
                  <div className="flex shrink-0 items-center gap-3 md:gap-8">
                    <span
                      role="img"
                      className="flex w-16 items-center justify-center md:w-24 md:justify-end"
                      aria-label={
                        spotMeInsurance
                          ? `SpotMe sí ofrece ${title}`
                          : `SpotMe no ofrece ${title}`
                      }
                    >
                      <SpotMeMark on={spotMeInsurance} />
                    </span>
                    <span
                      role="img"
                      className="flex w-16 items-center justify-center md:w-24 md:justify-end"
                      aria-label={
                        otherInsurance
                          ? `Otros sí ofrecen ${title}`
                          : `Otros no ofrecen ${title}`
                      }
                    >
                      <OtherMark on={otherInsurance} />
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-[12px] leading-relaxed text-ink-200">
              {/* Space folded into the template literal — see "Trigger H". */}
              {`${t('COMPARISON.INFO.TEXT_1')} `}
              <Link
                href={RedirectUrls.Help}
                className="font-medium text-blue-500 underline underline-offset-4 hover:text-blue-700"
              >
                {t('COMPARISON.INFO.LINK')}
              </Link>
            </p>
          </div>
        </section>
      </div>
    </AnimatedSection>
  )
}

export default React.memo(Comparison)
